import type { AlarmEvent, AlarmState } from '@ctcells/contract'
import { t, type MessageKey } from '@ctcells/i18n'

import type { RowTone } from '../../atoms/DataTable.tsx'
import { gate, type ActionGate, type ScreenAccess } from '../access.ts'
import { errorMessageOf } from '../format.ts'

/**
 * 알람 [확인]·[해제]의 판정 — 목록 행 끝(펼침 모드)과 오른쪽 서랍(통합 콘솔)이 **같은 함수**를 쓴다.
 * 두 자리가 판정을 따로 가지면 한쪽만 고쳐지는 날 같은 알람에서 버튼이 다르게 선다.
 *
 * 🔴 확인·해제는 `control` 권한이다(계약 `x-required-role: operator/admin` · 서버 `WRITE_ROLES`).
 *    **제어락은 요구하지 않는다**(계약에 `ControlToken` 파라미터가 없다 — `app/views/alarms.py` 머리말).
 * 🔴 서버에 `allowed_commands` 류가 없다 — 알람 응답의 상태 필드(`state`·`ack_required`·`resolved`)가 근거다.
 */
export function ackGate(access: ScreenAccess, alarm: AlarmEvent): ActionGate {
  return gate(access, {
    perm: 'control',
    stateOk: alarm.state === 'active' && alarm.ack_required !== false,
    /*
     * 🔴 사유 차례: 해제됨 → 확인 불요 → 이미 확인(eric 2026-09-28 「고쳐」). 해제된 알람은 확인 없이 자동 해제된
     *    것일 수도 있어(`auto_clear`) 「이미 확인했습니다」가 사실이 아닐 수 있다 — 상태 그대로 「이미 해제」다.
     */
    stateReason:
      alarm.state === 'cleared'
        ? 'reason.alarmCleared'
        : alarm.ack_required === false
          ? 'reason.ackNotRequired'
          : 'reason.alarmAcked',
  })
}

/** ⚠ [해제] 버튼을 «낼지»(latching = `auto_clear === false` 만)는 그리는 쪽이 정한다. 여기는 눌릴 수 있는지만 답한다 */
export function clearGate(access: ScreenAccess, alarm: AlarmEvent): ActionGate {
  return gate(access, {
    perm: 'control',
    stateOk: alarm.resolved === true && alarm.state !== 'cleared',
    /*
     * 🔴 **상태가 조건보다 먼저다**(eric 2026-09-28 「고쳐」). 종전에는 `cleared` 를 따로 가르지 않아
     *    이미 해제된 알람의 [해제]가 「조건이 아직 해소되지 않았습니다」로 섰다 — 사실과 반대다.
     */
    stateReason: alarm.state === 'cleared' ? 'reason.alarmCleared' : 'reason.alarmUnresolved',
  })
}

/** 알람 제목 — 사전이 덮어쓴 문구, 없으면 서버 문구. 목록 「알람」 칸과 서랍 「내용」 줄이 같은 글자여야 한다 */
export function alarmTitleOf(alarm: AlarmEvent): string {
  return errorMessageOf(alarm.error_code, alarm.message)
}

/** 제목 + 되풀이 수(`×3`) — 목록 「알람」 칸의 굵은 줄과 서랍 「내용」 줄이 같은 글자다 */
export function alarmHeadingOf(alarm: AlarmEvent): string {
  const count = alarm.occurrence_count ?? 1
  return `${alarmTitleOf(alarm)}${count > 1 ? ` ×${String(count)}` : ''}`
}

/**
 * 표의 상태 칸 표기. 🔴 **`Record<AlarmState, …>` 라 값이 늘면 타입이 먼저 걸린다.**
 *
 * 이 표가 생긴 까닭 — 종전에는 이 자리가 `state === 'active' ? 미확인 : 확인됨` **삼항 하나**였다.
 * 탭이 `state` 3축으로 바뀐 뒤에도 그 줄만 이진으로 남아 **`cleared` 가 「확인됨」으로 떨어졌다**
 * (2026-09-20 실서버: `cleared` 3건 · `acknowledged` 1건인데 표 4행이 전부 「확인됨」).
 * 삼항은 빠진 값을 말해 주지 않는다 — 표로 바꾸면 컴파일러가 대신 세어 준다.
 *
 * ⚠ `active` 만 탭(「활성」)과 낱말이 다르다(「미확인」). 탭은 **거르는 축 이름**이고 이 칸은
 *   **그 행이 사람에게 요구하는 것**을 말한다 — 일부러 다르다. 통일할지는 화면 문구 판단이라
 *   임의로 바꾸지 않았다.
 */
const ALARM_STATE_LABEL: Record<AlarmState, MessageKey> = {
  active: 'alarm.unacknowledged',
  acknowledged: 'alarm.acknowledged',
  cleared: 'alarm.cleared',
}

/**
 * 상태 글자 — 목록 「상태」 칸과 서랍 「상태」 줄이 같은 함수를 쓴다. 🔴 **알람 상태(`state`)만** 말한다.
 * ⚠ 종전에는 latching 이면 뒤에 「 · 조건 유지」를 이어 썼다 — 두 축이 한 칸이었다. eric 2026-10-30 「상태 컬럼에 두 개가
 *   같이 있는데 이거 분리해줘」로 조건은 {@link alarmConditionText} 의 제 칸으로 갔다. 다시 잇지 마라.
 */
export function alarmStateText(alarm: AlarmEvent): string {
  return t(ALARM_STATE_LABEL[alarm.state])
}

/**
 * 조건 글자(「조건」 칸 · 서랍 「조건」 줄) — 유지 · 해소. 🔴 **latching(`auto_clear === false`)에서만** 값이 있다.
 * 자동 소멸 알람에서 `resolved` 는 [해제] 전제가 아니라 읽는 사람을 헷갈리게 한다(07 §4.5 · 종전 이어 쓰기와 같은 판정).
 * 그 밖에는 `undefined` — 칸을 **비운다**(`—` 를 깔지 않는다).
 */
export function alarmConditionText(alarm: AlarmEvent): string | undefined {
  if (alarm.auto_clear !== false) return undefined
  return alarm.resolved === true ? t('alarm.condition.resolved') : t('alarm.condition.held')
}

/**
 * 행의 결 — **해제된 행만 글자를 흐리게** 한다.
 * 🔴 왼쪽 띠(활성 오류 · 긴급 = 위험 띠 · 활성 경고 = 주의 띠 · 디자인팀 시안 2026-10-01 §4)는 걷었다 — eric 2026-10-12
 *    「왼쪽 선 테두리 선 없애고. 심각도에. 경고에 색상을 넣어줘」. 색은 심각도 배지가 진다(`alarmSeverityTone`).
 *    띠를 되살리지 마라.
 */
export function alarmRowTone(alarm: AlarmEvent): RowTone | undefined {
  return alarm.state === 'cleared' ? 'muted' : undefined
}

/**
 * 「모두 해제」의 대상 — 받은 목록 중 **해제되지 않은 것**(활성 + 확인됨).
 *
 * 🔴 **조건이 유지 중인 latching 알람(`auto_clear === false` · `resolved !== true`)은 «대상에서 뺀다»(`held`).**
 *    서버 `clear` 는 `resolved` 를 가드하지 않는다(`ctcells-core app/services/alarms.py` `clear` 주석 — 「활성 조건
 *    가드는 `07 §4.5` 화면 몫」). 건별 [해제]가 `clearGate` 로 그 알람을 막는데, 일괄이 그 가드를 우회하면 안 된다.
 *    그래서 판정은 `clearGate` 와 같은 축(`resolved`)이고, 뺀 건수는 확인 창이 말한다.
 * ⚠ 자동 소멸 알람(`auto_clear !== false`)은 건별 [해제] 단추가 없지만 서버는 해제를 받는다 — 일괄은 이들을 해제한다
 *   (eric 「한번에 알람 없애게」).
 */
export function bulkClearTargets(alarms: readonly AlarmEvent[]): {
  targets: AlarmEvent[]
  held: AlarmEvent[]
} {
  const open = alarms.filter((alarm) => alarm.state !== 'cleared')
  const isHeld = (alarm: AlarmEvent) => alarm.auto_clear === false && alarm.resolved !== true
  return { targets: open.filter((a) => !isHeld(a)), held: open.filter(isHeld) }
}

/** [모두 해제] 단추 — 권한(`control` · 서버 `WRITE_ROLES`)이 먼저, 그다음 대상 0건. 숨기지 않는다 */
export function bulkClearGate(access: ScreenAccess, targetCount: number): ActionGate {
  return gate(access, {
    perm: 'control',
    stateOk: targetCount > 0,
    stateReason: 'reason.noAlarmsToClear',
  })
}
